import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { TextField, Box, FormControl } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DSText } from '@engenharia/ds-react';
import decode from 'jwt-decode';

import Logo from '../../components/molecules/Logo';
import Notification from '../../components/Notification';
import MetaHeader from '../../components/MetaHeader';
import { Container, Card, FormContainer, LinkButton } from './styles';
import Button from '../../components/atoms/Button';
import Header from '../../components/Header';
import { hasOtpToken, logout, getOtpToken } from '../../services/auth';
import { redirectUser } from '../../services/user';
import CircularLoader from '../../components/CircularLoader';

import authApi from '../../services/authApi';
import { getLoginCallbackUrl } from '../../utils/mcpOAuthParams';

const RESEND_COUNTDOWN = 30;

export default function Verify() {
  const history = useHistory();
  const { t } = useTranslation();

  const [code, setCode] = useState('');
  const [otpName, setOtpName] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [countdown, setCountdown] = useState(0);

  const [notification, setNotification] = useState({});
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const isCountingDown = countdown > 0;

  useEffect(() => {
    if (!hasOtpToken()) {
      history.push('/accounts/sign_in');
      return;
    }
    const decoded = decode(getOtpToken());
    setOtpName(decoded?.otp_name);
    setMaskedPhone(decoded?.maskedPhone || '');
    setMaskedEmail(decoded?.maskedEmail || '');

    if (decoded?.otp_name === 'email' || decoded?.otp_name === 'whatsapp') {
      setCountdown(RESEND_COUNTDOWN);
    }
  }, []);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const sendCode = useCallback(
    async (channel) => {
      try {
        const response = await authApi.post('/accounts/otp_send', { channel });
        const { channel: sentChannel, fallback } = response.data || {};

        let message;
        if (fallback) {
          message = t('verify.fallbackNotice');
        } else if (sentChannel) {
          message = t(`verify.codeSent.${sentChannel}`);
        } else {
          message = t('verify.codeSended');
        }

        setNotification({ key: new Date().getTime(), message });
        setCountdown(RESEND_COUNTDOWN);
      } catch (error) {
        if (error?.response?.status === 401) {
          logout();
          history.push('/accounts/signin');
        } else {
          setNotification({
            key: new Date().getTime(),
            message: error?.response?.data?.message || t('errors.connection-failure'),
          });
        }
      }
    },
    [t, history],
  );

  const handleChange = (event) => {
    setCode(event.target.value.replace(/\D/g, ''));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loadingSubmit) return;
    if (!code) {
      setNotification({ key: new Date().getTime(), message: t('errors.login-invalid') });
      return;
    }
    setLoadingSubmit(true);
    try {
      const response = await authApi.post('/accounts/otp_verify', {
        code,
        callback_url: getLoginCallbackUrl(),
      });
      await redirectUser(response);
    } catch (error) {
      setLoadingSubmit(false);
      if (error?.response?.status === 401) {
        logout();
        history.push('/accounts/signin');
      } else {
        setNotification({
          key: new Date().getTime(),
          message: error?.response?.data?.message || t('errors.connection-failure'),
        });
      }
    }
  };

  const getSubtitle = () => {
    if (otpName === 'whatsapp') return t('verify.subtitle.whatsapp', { phone: maskedPhone });
    if (otpName === 'email') return t('verify.subtitle.email', { email: maskedEmail });
    return t(`verify.subtitle.${otpName}`);
  };

  const renderResendActions = () => {
    if (otpName === 'app') {
      return (
        <LinkButton onClick={() => sendCode()} variant="button" color="secondary">
          {t('verify.sendCode.app')}
        </LinkButton>
      );
    }

    if (isCountingDown) {
      return (
        <DSText
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16, textAlign: 'center' }}
        >
          {t('verify.countdown', { seconds: countdown })}
        </DSText>
      );
    }

    if (otpName === 'whatsapp') {
      return (
        <>
          <LinkButton onClick={() => sendCode()} variant="button" color="secondary">
            {t('verify.sendCode.whatsapp')}
          </LinkButton>
          <br />
          <br />
          <LinkButton onClick={() => sendCode('email')} variant="button" color="secondary">
            {t('verify.sendCode.emailFallback')}
          </LinkButton>
        </>
      );
    }

    return (
      <LinkButton onClick={() => sendCode()} variant="button" color="secondary">
        {t(`verify.sendCode.${otpName}`)}
      </LinkButton>
    );
  };

  return (
    <>
      <Header positionFixed />
      <Container>
        <MetaHeader
          title="Autenticação de dois fatores - mLabs | Gestão de redes sociais"
          description="mLabs - Gestão de Redes Sociais | Autenticação de dois fatores"
        />
        <Logo />
        <Box mx={2} my={8} component={Card}>
          <DSText variant="h3" color="primary">
            {t('verify.title')}
          </DSText>
          <Box my={3}>
            <DSText variant="body2" color="textPrimary">
              {getSubtitle()}
            </DSText>
          </Box>
          <FormContainer>
            <form onSubmit={handleSubmit}>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  inputProps={{ required: true, maxLength: 6 }}
                  type="text"
                  id="code"
                  name="code"
                  label={t('verify.input.code')}
                  value={code}
                  onChange={handleChange}
                  variant="outlined"
                  autoComplete="off"
                  margin="normal"
                  autoFocus
                />
              </FormControl>

              <FormControl fullWidth>
                <Button
                  mt={4}
                  type="submit"
                  fullWidth
                  disabled={code.length !== 6 || loadingSubmit}
                  data-testid="formSubmit"
                  endIcon={loadingSubmit && <CircularLoader scale="0.4" />}
                >
                  {!loadingSubmit && t('verify.button')}
                </Button>
              </FormControl>
            </form>
            {renderResendActions()}
          </FormContainer>
        </Box>
        {notification?.key && (
          <Notification
            key={notification.key}
            index={notification.key}
            message={notification.message}
          />
        )}
      </Container>
    </>
  );
}
